/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 * Page dashboard translation constants
 *
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.34.0
 */

import { useState, useRef, useCallback, useMemo } from 'react';

import { Menu, Typography } from '@nexign/scc-ui-kit';
import includes from 'lodash/includes';
import { useDispatch, useSelector } from 'react-redux';

import { icons } from '@/main/assets';
import { ImgStyled } from '@/main/components/common/Block/Img';
import PageLocale from '@/main/modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@/main/modules/internationalization/constants';
import { makeSelectTranslationFastActions } from '@/main/modules/internationalization/intSelectors';
import { modalOpen } from '@/main/modules/modal/modalActions';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { openRoute } from '@/main/routes/constants/externalRoutes';

import { EFastActionsButtonTypes, maxVisibleButtonsOnPlate } from './constants/constants';
import { FastActionButton } from './FastActionButton';
import { FastActionsModal } from './FastActionModal';
import { buttonsArray, getRoamSubsText, mapFastActionButtonTypeToUrl, openRoamSubscribersTable } from './fastActionsButtonsUtils';
import { ModalTextToButtons } from './ModalTextToButtons';
import { useGetFastActionsQuery } from './store';
import { 
    FastActionsPlateLayoutStyled, 
    FastActionsPlateDisplayContainerStyled, 
    VerticalSeparateLineStyled,
    FastActionButtonsArrayStyled,
    FastActionButtonStyled,
} from './styled/FastActionsPlateStyled'


export const FastActionsPlate = () => {
    const dispatch = useDispatch();
    const translation = useSelector(makeSelectTranslationFastActions());
    const isMainOperationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.MAIN_OPERATIONS));

    const [menuOpen, setMenuOpen] = useState(false);
    const menuAnchorRef = useRef<HTMLButtonElement | null>(null);

    const { data: fastActionsData } = useGetFastActionsQuery();

    const fastActionsButtonsAvailable = fastActionsData?.buttons;
    const subscribersInRoamingCount = fastActionsData?.subscribersInRoamingCount ?? 0;

    const availableButtonsArray = 
        useMemo(() => 
            buttonsArray({ translation }).filter(item => includes(fastActionsButtonsAvailable, item.buttonType)),
        [fastActionsButtonsAvailable, translation]);

    const visibleButtonsArray = 
        useMemo(() => 
            availableButtonsArray.slice(0, maxVisibleButtonsOnPlate), 
        [availableButtonsArray]);

    const moreButtonsArray = 
        useMemo(() => 
            availableButtonsArray.slice(maxVisibleButtonsOnPlate),
        [availableButtonsArray]);

    const handleMenuOpen = useCallback(() => setMenuOpen(true), []);
    const handleMenuClose = useCallback(() => setMenuOpen(false), []);

    const roamSubsText = useMemo(() => 
        getRoamSubsText(subscribersInRoamingCount, translation), 
    [subscribersInRoamingCount, translation]);

    const handleOpenModal = useCallback((buttonType: EFastActionsButtonTypes) => {
        handleMenuClose();
        const modalText = ModalTextToButtons({ translation, buttonType });
        const url = mapFastActionButtonTypeToUrl[buttonType];

        if (isMainOperationsEnabled) {
            openRoute(url);
        } else {
            dispatch(
                modalOpen({
                    render: () => (
                        <FastActionsModal modalText={modalText}/>
                    ),
                }),
            );
        }
    },[dispatch, handleMenuClose, isMainOperationsEnabled, translation]);

    // если рычаг включен, но массив кнопок отсутствует
    if (!fastActionsButtonsAvailable?.length) 
        return <></>;

    return (
        <FastActionsPlateLayoutStyled>
                <PageLocale lazy
                    fileNames={[
                        TranslationFileNamesEnum.fastActions,
                    ]}/>
                <FastActionsPlateDisplayContainerStyled>
                    <FastActionButtonsArrayStyled>
                        {visibleButtonsArray.map( (item, index) => 
                            <>
                                {!!index && <VerticalSeparateLineStyled key={index} />}
                                {item.buttonType === EFastActionsButtonTypes.SUBSCRIBERS_IN_ROAMING
                                    ? <FastActionButton 
                                        buttonIcon={item.buttonIcon} 
                                        buttonText={roamSubsText}
                                        buttonType={EFastActionsButtonTypes.SUBSCRIBERS_IN_ROAMING}
                                        disabled={!subscribersInRoamingCount}
                                        onClick={() => openRoamSubscribersTable(dispatch)}
                                    />
                                    : <FastActionButton
                                        buttonIcon={item.buttonIcon}
                                        buttonText={item.buttonText} 
                                        buttonType={item.buttonType} 
                                        onClick={() => handleOpenModal(item.buttonType)}
                                    />}
                                
                            </>,
                        )}
                    </FastActionButtonsArrayStyled>
                    {!!moreButtonsArray?.length &&
                        (
                            <>
                                <FastActionButtonStyled
                                    className="fast-actions-button"
                                    color='white'
                                    ref={menuAnchorRef}
                                    size="small" 
                                    variant="contained" 
                                    onClick={handleMenuOpen}
                                >
                                    <>
                                        <Typography variant="body2">{translation.fastActionsPlate.more}</Typography>
                                        <ImgStyled className="small" src={menuOpen ? icons.arrowListUpSmall : icons.arrowListDownSmall} />
                                    </>
                                </FastActionButtonStyled>
                                <Menu
                                    anchorEl={menuAnchorRef.current}
                                    disablePortal={false}
                                    open={menuOpen}
                                    placement="bottom"
                                    style={{ zIndex: 501 }}
                                    onClose={handleMenuClose}
                                >
                                    {
                                        moreButtonsArray.map( item => 
                                            <FastActionButton
                                                buttonIcon={item.buttonIcon}
                                                buttonText={item.buttonText} 
                                                buttonType={item.buttonType}
                                                key={item.buttonType}
                                                onClick={() => handleOpenModal(item.buttonType)}
                                            />,
                                        )
                                    }                    
                                </Menu>
                            </>
                        )
                    }
                </FastActionsPlateDisplayContainerStyled>
        </FastActionsPlateLayoutStyled>
    )
}
